/**
 * First Interviews bulk invite — drafting a role's whole batch.
 *
 * Sending itself reuses `sendCandidateEmail` (taOutboundApi.ts) once per
 * candidate: the proxy resolves each recipient from Lever, so a batch is N
 * individually-addressed emails, never one message with N recipients.
 *
 * Own file rather than taActionBoardApi.ts, which is at the 500-line limit.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type { BryqKey } from './bryqApi';
import type { InterviewLinkOption } from './inviteDraftApi';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

const BASE = `${PROXY_URL}/api/ta-action-board`;

/** One `{token}` in the template and what it resolved to for this candidate. */
export interface InviteVariable {
  token: string;
  value: string;
  /** false = nothing resolved it, so the raw "{token}" is still in the text. */
  resolved: boolean;
}

export interface FirstInvitePreviewCandidate {
  /** Set by the invite dialog: a Bryq test to send right after this invite went out. */
  bryq?: BryqKey | null;
  opportunityId: string;
  name: string;
  candidateEmail?: string;
  stageId: string;
  stageName: string;
  subject?: string;
  body?: string;
  /** Where this candidate's copy came from — see FirstInviteTemplate.source. */
  source?: 'link' | 'library' | 'default';
  bookingUrl?: string;
  linkId?: string | null;
  meetingType?: '1:1' | 'panel' | null;
  nextInterviewers?: { email: string; name: string; title: string }[];
  variables: InviteVariable[];
  /**
   * EVERY resolved token value for this candidate, not only the ones the
   * template currently uses — the modal re-renders locally as the recruiter
   * edits, and a token they add must resolve without another round trip.
   */
  vars?: Record<string, string>;
  /** Codes; phrased for the recruiter by WARNING_TEXT in the modal. */
  warnings: string[];
  /** Set when the candidate left the first-interview stages since the board loaded. */
  skipped?: string;
}

/** The token-bearing template one stage's batch shares, before substitution. */
export interface FirstInviteTemplate {
  stageId: string;
  stageName: string;
  /** The link every candidate in this stage is drafted on — the derived default or the pick. */
  linkId: string | null;
  /** Every live link for the stage; the picker shows only when there is more than one. */
  linkOptions?: InterviewLinkOption[];
  /** The role is closed in Lever, so no link is offered. */
  roleClosed?: boolean;
  /** 'link' = authored on this link; 'library' = the stage's Email Library entry. */
  source: 'link' | 'library' | 'default';
  /** The Email Library entry's name + key, when the copy came from there. */
  label?: string | null;
  templateKey?: string | null;
  subject: string;
  body: string;
  tokens: string[];
}

export interface FirstInvitePreview {
  language: 'de' | 'en';
  candidates: FirstInvitePreviewCandidate[];
  templates: FirstInviteTemplate[];
}

/**
 * Draft every selected candidate's invitation. Mints (or reuses) each
 * candidate's booking token so the preview shows the real URL — nothing is sent.
 */
export async function fetchFirstInvitePreview(payload: {
  postingId: string;
  opportunityIds: string[];
  roleTitle?: string;
  boardEmail: string;
  senderName?: string;
  language: 'de' | 'en';
  /** `{ [stageId]: linkId }` — overrides only; a stage left out gets the derived default. */
  linkChoices?: Record<string, string>;
}): Promise<FirstInvitePreview> {
  const res = await authenticatedFetch(`${BASE}/first-invite-preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error || 'Could not draft the invitations');
  }
  return res.json() as Promise<FirstInvitePreview>;
}
